// frontend/src/components/ui/pipeline-visualization.jsx
// Pipeline visualization component for sales pipeline - Funnel Chart
import React from 'react';
import { cn } from '../../lib/utils';
import { useBranding } from '../../contexts/BrandingContext';

export const PipelineVisualization = ({ data, className = '' }) => {
  const { branding } = useBranding();
  const primaryColor = branding?.primaryColor || '#3b82f6';
  const secondaryColor = branding?.secondaryColor || '#10b981';
  // data should be an array of { stage: string, count: number, value?: number }
  if (!data || data.length === 0) {
    return (
      <div className={cn("flex items-center justify-center h-32 text-muted-foreground", className)}>
        <p className="text-sm">No pipeline data available</p>
      </div>
    );
  }

  const totalCount = data.reduce((sum, item) => sum + (item.count || 0), 0);
  const totalValue = data.reduce((sum, item) => sum + (item.value || 0), 0);
  const maxCount = Math.max(...data.map(item => item.count || 0));

  // Use a single color (unicolor) for all pipeline stages
  // This creates a cleaner, more cohesive funnel visualization
  const pipelineColor = primaryColor; // Use branding primary color for all stages

  const stageLabels = {
    leads: 'Leads',
    new: 'Leads',
    contacted: 'Contacted',
    qualified: 'Qualified',
    proposal: 'Proposal',
    negotiation: 'Negotiation',
    closed_won: 'Closed-Won',
    won: 'Closed-Won',
    converted: 'Closed-Won',
    lost: 'Lost'
  };

  return (
    <div className={cn("space-y-6", className)}>
      {/* Funnel Chart - Vertical bars getting smaller */}
      <div className="space-y-3">
        {data.map((stage, index) => {
          const stageKey = stage.stage?.toLowerCase() || stage.name?.toLowerCase() || 'new';
          const label = stageLabels[stageKey] || stage.stage || stage.name;
          const count = stage.count || 0;
          const widthPercentage = maxCount > 0 ? (count / maxCount) * 100 : 0;
          
          return (
            <div key={index} className="flex items-center gap-4">
              {/* Stage Label - Left */}
              <div className="w-24 text-sm font-medium text-muted-foreground flex-shrink-0">
                {label}
              </div>
              
              {/* Funnel Bar - Centered (like reference image) */}
              <div className="flex-1 relative">
                <div className="h-6 rounded-md overflow-hidden bg-muted flex items-center justify-center">
                  <div
                    className="h-full rounded-md transition-all duration-500"
                    style={{ 
                      width: `${Math.max(widthPercentage, 5)}%`,
                      backgroundColor: pipelineColor
                    }}
                  />
                </div>
              </div>
              
              {/* Count - Right */}
              <div className="w-20 text-right flex-shrink-0">
                <div className="text-lg font-bold text-foreground">{count}</div>
                {stage.value !== undefined && stage.value > 0 && (
                  <div className="text-xs text-muted-foreground mt-0.5">
                    €{(stage.value || 0).toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
